import React, { useState } from "react";
import { useCart } from "../contexts/CartContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  ShoppingCart, 
  CreditCard, 
  Loader2,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  MapPin,
  User,
  Mail,
  Phone,
  Truck
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import SEOHead from "../components/SEOHead";

const CheckoutPage = () => {
  const { state } = useCart();
  const navigate = useNavigate();
  
  // Australian states and territories
  const australianStates = [
    { code: "NSW", name: "New South Wales" },
    { code: "VIC", name: "Victoria" },
    { code: "QLD", name: "Queensland" },
    { code: "WA", name: "Western Australia" },
    { code: "SA", name: "South Australia" },
    { code: "TAS", name: "Tasmania" },
    { code: "ACT", name: "Australian Capital Territory" },
    { code: "NT", name: "Northern Territory" }
  ];

  const [customerInfo, setCustomerInfo] = useState({
    // Customer Details
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    
    // Delivery Address
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "NSW", // Default to NSW
    postalCode: "",
    country: "Australia", // Fixed to Australia
    deliveryNotes: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Calculate totals
  const subtotal = (state?.items || []).reduce((sum, item) => {
    const price = parseFloat(item.product.price || item.product.regular_price || 0);
    return sum + (price * item.quantity);
  }, 0);
  
  const shippingFee = 15.00; // Fixed $15 shipping to Australia
  const total = subtotal + shippingFee;
  const totalItems = (state?.items || []).reduce((sum, item) => sum + item.quantity, 0);

  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const required = [
      'firstName', 'lastName', 'email', 'phone',
      'addressLine1', 'city', 'state', 'postalCode'
    ];
    
    for (let field of required) {
      if (!customerInfo[field].trim()) {
        const fieldName = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        setError(`Please enter your ${fieldName.toLowerCase()}`);
        return false;
      }
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerInfo.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    
    // Australian postal code validation (4 digits)
    const postalRegex = /^\d{4}$/;
    if (!postalRegex.test(customerInfo.postalCode)) {
      setError("Please enter a valid 4-digit Australian postal code");
      return false;
    }
    
    return true;
  };

  const handleCheckout = async () => {
    setError("");
    setSuccess("");
    
    if (!validateForm()) {
      return;
    }

    if ((!state?.items || state.items.length === 0)) {
      setError("Your cart is empty");
      return;
    }

    setLoading(true);

    try {
      // Prepare order data with delivery address
      const orderData = {
        customer: {
          first_name: customerInfo.firstName,
          last_name: customerInfo.lastName,
          email: customerInfo.email,
          phone: customerInfo.phone
        },
        shipping_address: {
          first_name: customerInfo.firstName,
          last_name: customerInfo.lastName,
          address_line_1: customerInfo.addressLine1,
          address_line_2: customerInfo.addressLine2,
          city: customerInfo.city,
          state: customerInfo.state,
          postal_code: customerInfo.postalCode,
          country: customerInfo.country,
          phone: customerInfo.phone
        },
        items: (state?.items || []).map(item => ({
          product: {
            id: item.product.id || item.product._id,
            name: item.product.name,
            sku: item.product.sku,
            images: item.product.images || []
          },
          quantity: item.quantity,
          price: parseFloat(item.product.price || item.product.regular_price || 0),
          metal_type: item.metal_type || null
        })),
        customer_notes: customerInfo.deliveryNotes,
        origin_url: window.location.origin
      };

      // Create checkout session with full address
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to create checkout session");
      }

      const checkoutData = await response.json();
      
      // For now, show success message (integrate with Stripe later)
      setSuccess("Order submitted successfully! We'll contact you soon to process your payment.");
      
      // Clear form after successful submission
      setTimeout(() => {
        navigate("/opal-store");
      }, 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price || 0);
    return numPrice.toFixed(2);
  };

  if ((!state?.items || state.items.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <SEOHead
          title="Checkout - Umoona Opal Mine"
          description="Complete your purchase of authentic Australian opals from Umoona Opal Mine, Coober Pedy."
          canonical="/checkout"
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16">
            <ShoppingCart className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Your cart is empty</h1>
            <p className="text-gray-600 mb-8">Add some beautiful opals to your cart to continue</p>
            <Button asChild className="mining-cta">
              <Link to="/opal-store">
                Continue Shopping
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <SEOHead
        title="Checkout - Umoona Opal Mine"
        description="Complete your purchase of authentic Australian opals from Umoona Opal Mine, Coober Pedy."
        canonical="/checkout"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="mb-8">
          <Button variant="outline" className="mb-4" asChild>
            <Link to="/opal-store">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Store
            </Link>
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
              <p className="text-gray-600">Complete your purchase of {totalItems} items</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600">{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-600">{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Customer Information & Delivery Address */}
          <div className="space-y-6">
            
            {/* Customer Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2 text-purple-600" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="John"
                      value={customerInfo.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Smith"
                      value={customerInfo.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john.smith@example.com"
                    value={customerInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="04XX XXX XXX"
                    value={customerInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                  />
                </div>
              </CardContent>
            </Card>

            {/* Delivery Address */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-purple-600" />
                  Delivery Address
                </CardTitle>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mt-3">
                  <p className="text-sm text-blue-800 flex items-center">
                    <Truck className="w-4 h-4 mr-2" />
                    We ship Australia-wide with a flat $15.00 shipping fee
                  </p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <Input
                    id="addressLine1"
                    type="text"
                    placeholder="123 Main Street"
                    value={customerInfo.addressLine1}
                    onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 mb-2">
                    Apartment, Unit, etc. (Optional)
                  </label>
                  <Input
                    id="addressLine2"
                    type="text"
                    placeholder="Unit 5"
                    value={customerInfo.addressLine2}
                    onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City/Suburb *
                    </label>
                    <Input
                      id="city"
                      type="text"
                      placeholder="Sydney"
                      value={customerInfo.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-2">
                      Postal Code *
                    </label>
                    <Input
                      id="postalCode"
                      type="text"
                      placeholder="2000"
                      maxLength="4"
                      value={customerInfo.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                    State/Territory *
                  </label>
                  <Select value={customerInfo.state} onValueChange={(value) => handleInputChange('state', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {australianStates.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name} ({state.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <Input
                    id="country"
                    type="text"
                    value="Australia"
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">We currently ship to Australia only</p>
                </div>
                
                <div>
                  <label htmlFor="deliveryNotes" className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Instructions (Optional)
                  </label>
                  <textarea
                    id="deliveryNotes"
                    className="w-full p-3 border border-gray-300 rounded-md resize-none"
                    rows="3"
                    placeholder="Special delivery instructions, gate codes, etc."
                    value={customerInfo.deliveryNotes}
                    onChange={(e) => handleInputChange('deliveryNotes', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ShoppingCart className="w-5 h-5 mr-2 text-purple-600" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(state?.items || []).map((item) => {
                    const price = parseFloat(item.product.price || item.product.regular_price || 0);
                    const itemTotal = price * item.quantity;
                    const productId = item.product.id || item.product._id;
                    
                    return (
                      <div key={productId} className="flex items-center gap-4 py-3 border-b border-gray-200 last:border-b-0">
                        <div className="w-16 h-16 bg-white rounded-md overflow-hidden flex-shrink-0">
                          {item.product.images && item.product.images.length > 0 ? (
                            <img
                              src={item.product.images[0].src}
                              alt={item.product.name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.src = "https://placehold.co/64/64";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ShoppingCart className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 truncate">
                            {item.product.name}
                          </h4>
                          {item.metal_type && (
                            <p className="text-sm font-medium text-purple-600">
                              {item.metal_type === 'sterling_silver' ? 'Sterling Silver' : 'Sterling Silver Gold Plated'}
                            </p>
                          )}
                          <p className="text-sm text-gray-600">
                            ${formatPrice(price)} × {item.quantity}
                          </p>
                        </div>
                        
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            ${formatPrice(itemTotal)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Separator className="my-6" />

                <div className="space-y-3">
                  <div className="flex justify-between text-base">
                    <span>Subtotal ({totalItems} items):</span>
                    <span>${formatPrice(subtotal)}</span>
                  </div>
                  
                  <div className="flex justify-between text-base">
                    <span>Shipping to Australia:</span>
                    <span>${formatPrice(shippingFee)}</span>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-purple-600">${formatPrice(total)}</span>
                  </div>
                </div>

                <div className="mt-6">
                  <Button
                    onClick={handleCheckout}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Place Order - ${formatPrice(total)}
                      </>
                    )}
                  </Button>
                  
                  <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500">
                      🔒 Secure checkout • We'll contact you to process payment
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Shipping Info */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-gray-900 mb-3">📦 Shipping Information</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• $15.00 flat rate shipping Australia-wide</p>
                  <p>• Orders processed within 1-2 business days</p>
                  <p>• Tracking information provided</p>
                  <p>• Secure packaging for safe delivery</p>
                  <p>• Free shipping on orders over $100</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;